import { useId, useMemo, useState, type FormEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import { BondDetailsFields } from "@/components/BondDetailsFields";
import { TOUCH_SQUARE, buttonClass } from "@/components/buttonStyles";
import { toRenderFinalField, type BondValueBag } from "@/lib/bondFields";
import {
  EDIT_VOID_WARNING,
  bondEditIssues,
  changedBondValues,
  editBondSubmission,
  editableBag,
  editableFinalFields,
  issuedBidBond,
} from "@/lib/bondEdit";
import {
  readBondRows,
  readChangeRequest,
  readEditPolicy,
} from "@/lib/bondStateModel";
import {
  BOND_FINAL_STEP_KEY,
  type BondFinalField,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { VALIDATION_SUMMARY } from "@/lib/errors";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { useBondDocuments } from "@/lib/useBondDocuments";

// BRK-S419 — Edit Bond Request: a filed submission's bond details changed
// before award (D1 reverses BRK-S197's "after submit, nothing is editable").
//
// THE FORM IS THE WIZARD'S. Every final-step field renders through the ONE
// shared renderer, `BondDetailsFields` (BRK-S418), from the configuration —
// the same controls, required markers and D-10 rules as the project & bond
// terms step, prefilled from `values.final`. PMS's `editPolicy.locks` (field
// key → reason) render read-only beside their reason. A `files` field is shown
// READ-ONLY (BRK-S490): the order's documents by name and the way to the
// Submission Documents tab, where documents are added after submit; it is
// never part of what is sent (`editableFinalFields`).
//
// NOTHING IS DECIDED HERE (#7). The page opens the dialog only while
// `actions.edit` is granted; the void warning reads the served `bonds[]` (an
// issued bid bond); whether the edit is taken, what it voids and what the
// history says are the response's. "Save and submit changes" sends ONLY the
// changed fields (`changedBondValues`, in the contract's spelling), after a
// ConfirmDialog when it will void the issued bid bond. Success renders from
// the 2xx — the page shows the order view it answered with — and a 409/422
// renders inline with every input kept.
//
// Mounted only while open (the page's flag), so each opening starts from the
// order as it reads now and nothing outlives the dialog (#9).

const SUCCESS_EDIT = "Your changes were submitted for review.";
const SUCCESS_SAVED = "Your changes were saved.";
const NOTHING_CHANGED = "You haven’t changed anything yet.";

export function EditBondRequestDialog({
  order,
  fields,
  onClose,
  onUpdated,
  onOpenDocuments,
}: {
  order: BondSubmission;
  /** The configuration's final-step fields; null when the read failed. */
  fields: readonly BondFinalField[] | null;
  onClose: () => void;
  /** The order view a 2xx answered with: the page shows it. */
  onUpdated: (order: BondSubmission) => void;
  /** The Supporting documents field's link: open the Submission Documents
   *  tab (BRK-S490). */
  onOpenDocuments: () => void;
}) {
  const { authedRequest } = useAuth();
  const titleId = useId();
  const subId = useId();
  const formId = useId();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  const declared = useMemo(() => editableFinalFields(fields ?? []), [fields]);
  const { documents } = useBondDocuments(authedRequest, order.id);
  const documentNames =
    documents === null
      ? null
      : documents.filter((d) => d.status !== "required").map((d) => d.filename);
  const rendered = useMemo(() => declared.map(toRenderFinalField), [declared]);
  const [held] = useState<BondValueBag>(() =>
    editableBag(order.values?.[BOND_FINAL_STEP_KEY] ?? {}),
  );
  const [locks] = useState(() => readEditPolicy(order.editPolicy)?.locks ?? {});
  // The bond the warning is about, as the order read when the dialog opened.
  const [voids] = useState(() => issuedBidBond(order));

  const [values, setValues] = useState<BondValueBag>(held);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [answer, setAnswer] = useState<BondSubmission | null>(null);

  function onChange(name: string, value: BondValueBag[string]) {
    setValues((all) => ({ ...all, [name]: value }));
    setInvalid((all) => {
      const rest = { ...all };
      delete rest[name];
      return rest;
    });
  }

  /** The changed fields, or null when the form is not ready to send. */
  function checked() {
    const issues = bondEditIssues(rendered, values, locks);
    if (Object.keys(issues).length > 0) {
      setInvalid(issues);
      setError(VALIDATION_SUMMARY);
      return null;
    }
    const changed = changedBondValues(rendered, held, values, locks);
    if (Object.keys(changed).length === 0) {
      setError(NOTHING_CHANGED);
      return null;
    }
    return changed;
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    const changed = checked();
    if (changed === null) return;
    if (voids !== null) {
      setConfirming(true);
      return;
    }
    void send(changed);
  }

  async function send(changed: NonNullable<ReturnType<typeof checked>>) {
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      const fresh = await editBondSubmission(authedRequest, order.id, {
        values: changed,
      });
      onUpdated(fresh);
      setAnswer(fresh);
    } catch (err) {
      const anchored = anchorServerError(
        err,
        rendered.map((f) => f.name),
      );
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  // What the 2xx says happened — never what the dialog asked for.
  const voidedNow =
    answer !== null &&
    voids !== null &&
    readBondRows(answer.bonds).some(
      (b) => b.id === voids.id && b.status === "voided",
    );

  return (
    <>
      <Dialog
        open
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="md"
        fullScreen={phone}
        scroll="paper"
        // "Edit Bond Request SB-##### · {insured}"
        aria-labelledby={`${titleId} ${subId}`}
      >
        <div className="flex items-start gap-3 border-b border-brk-border p-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <h2 id={titleId} className="text-heading text-brk-text">
              Edit Bond Request
            </h2>
            <p id={subId} className="break-words text-sm text-brk-dim">
              {[order.number, order.accountLegalName]
                .filter((part): part is string => !!part)
                .join(" · ")}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            disabled={busy}
            className={`rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {answer !== null ? (
            <div role="status" className="flex flex-col gap-1 text-sm">
              <p className="text-green-700 dark:text-green-300">
                <span aria-hidden="true">✓ </span>
                {readChangeRequest(answer.changeRequest)?.kind === "edit"
                  ? SUCCESS_EDIT
                  : SUCCESS_SAVED}
              </p>
              {voidedNow && (
                <p className="text-brk-text">The bid bond was voided.</p>
              )}
            </div>
          ) : fields === null ? (
            <p role="alert" className="text-sm text-brk-danger">
              We couldn’t load this bond’s fields, so it can’t be edited here
              right now. Message Signal with the change.
            </p>
          ) : (
            <form
              id={formId}
              noValidate
              onSubmit={submit}
              className="flex min-w-0 flex-col gap-4"
            >
              <p className="text-sm text-brk-dim">
                Change the details below. Your changes go to Signal for review
                before they apply. Documents are added on the Submission
                Documents tab.
              </p>
              {voids !== null && (
                <p
                  role="note"
                  className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-400/35 dark:bg-amber-400/10 dark:text-amber-200"
                >
                  {EDIT_VOID_WARNING}
                </p>
              )}
              <BondDetailsFields
                // Every final-step field, the `files` one read-only (BRK-S490);
                // only `declared` fields are checked and sent.
                fields={fields}
                values={values}
                onChange={onChange}
                invalid={invalid}
                locks={locks}
                filesReadOnly={{
                  names: documentNames,
                  onOpenDocuments,
                }}
              />
              {error && (
                <p role="alert" className="text-sm text-brk-danger">
                  {error}
                </p>
              )}
            </form>
          )}
        </DialogContent>
        <DialogActions>
          {/* Stacked at 375 (the send on top), in a row from `sm:`. */}
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {answer !== null ? (
              <button type="button" onClick={onClose} className={buttonClass()}>
                Done
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={busy}
                  className={buttonClass({ variant: "secondary" })}
                >
                  Cancel
                </button>
                {fields !== null && (
                  <button
                    type="submit"
                    form={formId}
                    disabled={busy}
                    className={buttonClass()}
                  >
                    {busy ? "Submitting…" : "Save and submit changes"}
                  </button>
                )}
              </>
            )}
          </div>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirming}
        question="Void the issued bid bond?"
        confirmLabel="Void bid bond and submit"
        cancelLabel="Keep editing"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          const changed = checked();
          if (changed !== null) void send(changed);
        }}
      >
        <p className="text-sm text-brk-text">{EDIT_VOID_WARNING}</p>
      </ConfirmDialog>
    </>
  );
}
